import { memo, useCallback, useState, useEffect } from "react";
import Form from "../../components/Form.js";
import Table from "../../components/Table.js";
import {
    getOwners,
    getAvailableAnimalsAllTypes,
    getUserRole,
    updateAvailableAnimal,
    deleteAvailableAnimal,
} from "../../api/index.js";
import { useSnackbar } from "../../utils/index.js";
import useSelectStyles from "../../components/styles/Select.js";
import useListStyles from "../../components/styles/List.js";
import Spinner from "../../components/Spinner.js";
import { Box, Typography, Grid2, Modal, Slide, IconButton } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import {
    codeHeader,
    animalTypeHeader,
    animalValueHeader
} from "../../components/TableHeaders.js";
import AreYouSurePopup from "../../components/popups/AreYouSurePopup.js";

const typeTranslates = [
    { en: "maleAnimal", gr: "Κριός" },
    { en: "femaleAnimal", gr: "Προβατίνα" },
    { en: "lamb", gr: "Αμνός" },
]
const EditAvailableAnimals = () => {
    const [fetchedOwners, setFetchedOwners] = useState([]);
    const [ownersDropdownValues, setOwnersDropdownValues] = useState([]);
    const [isLoading, setIsLoading] = useState([]);
    const [step, setStep] = useState(1);
    const [selectedOwner, setSelectedOwner] = useState();
    const [fetchedAvailableAnimals, setFetchedAvailableAnimals] = useState([]);
    const [animalToUpdate, setAnimalToUpdate] = useState();
    const [isUpdateOpen, setIsUpdateOpen] = useState(false);
    const [userRole, setUserRole] = useState();
    const [areYouSureIsOpen, setAreYouSureIsOpen] = useState(false);

    const selectClasses = useSelectStyles();
    const listClasses = useListStyles();
    const { success, error } = useSnackbar();

    const fetchData = useCallback(
        async () => {
            setIsLoading(true);
            try {
                const { owners } = await getOwners();
                setFetchedOwners(owners);
                setOwnersDropdownValues(owners.map((owner) => ({
                    value: `${owner?.lastName} ${owner?.firstName} - ${owner?.code}`,
                    text: `${owner?.lastName} ${owner?.firstName} - ${owner?.code}`
                })));

                const { role } = await getUserRole();
                setUserRole(role);
            } catch (err) {
                error(err);
            }
            setIsLoading(false);
        }
    )

    useEffect(() => {
        fetchData();
    }, []);

    const handleAnimalCodeClick = (value) => {
        console.log(value);
        setAnimalToUpdate(fetchedAvailableAnimals.find((animal) => animal.code === value))
        setIsUpdateOpen(true);
    }
    const tableColumns = [
        codeHeader(handleAnimalCodeClick),
        animalTypeHeader,
        animalValueHeader
    ]

    const ownerFormContent = [
        {
            customType: "dropdown",
            id: "owner",
            items: ownersDropdownValues,
            placeholder: "Εκτροφή",
            required: true,
        },
        {
            customType: "button",
            id: "submit",
            type: "submit",
            text: "Επόμενο",
        },
    ];

    const updateAnimalFormContent = [
        {
            customType: "input",
            id: "code",
            type: "text",
            placeholder: "Κωδικός Ζώου",
            required: true,
            value: animalToUpdate?.code || null,
            disabled: true
        },
        {
            customType: "input",
            id: "value",
            type: "number",
            placeholder: "Τιμή (€)",
            required: true,
            value: animalToUpdate?.value || null,
        },
        {
            customType: "button",
            id: "submit",
            type: "submit",
            text: "Αποθήκευση",
        },
    ]

    const fetchAnimals = async (ownerId) => {
        const { success: scs, availableAnimals, message } = await getAvailableAnimalsAllTypes(ownerId);
        if (scs) {
            setFetchedAvailableAnimals(availableAnimals.map((animal) => {
                const translation = typeTranslates.find((t) => t.en === animal.type);
                return {
                    ...animal,
                    type: translation ? translation.gr : animal.type,
                }
            })
            );
            setStep(2);
        } else {
            error(message);
        }
    }

    const handleOwnerSubmit = (values) => {
        setIsLoading(true);
        const selOwner = fetchedOwners.find((owner) => `${owner?.lastName} ${owner?.firstName} - ${owner?.code}` === values.owner)
        setSelectedOwner(selOwner);
        fetchAnimals(selOwner._id);
        setIsLoading(false);
    };

    const handleAnimalUpdateSubmit = async (values) => {
        setIsLoading(true);
        const { success: scs, message } = await updateAvailableAnimal(values, selectedOwner._id, animalToUpdate.code);
        if (scs) {
            success(message);
            setAnimalToUpdate(null);
            fetchAnimals(selectedOwner._id)
        } else {
            error(message)
        }
        setIsUpdateOpen(false);
        setIsLoading(false);

    }

    const handleDelete = async () => {
        setIsLoading(true);
        const { success: scs, message } = await deleteAvailableAnimal(selectedOwner._id, animalToUpdate.code);
        if (scs) {
            success(message);
            setAreYouSureIsOpen(false);
            setIsUpdateOpen(false);
            setAnimalToUpdate(null);
            fetchAnimals(selectedOwner._id)
        } else {
            error(message);
        }
        setIsLoading(false);
    }

    return (
        <>
            <Spinner open={isLoading} />
            {step === 1 && (
                <Box className={selectClasses.outerBox}>
                    <Box container flexDirection="column" className={selectClasses.innerBox}>

                        <Typography variant="h5" sx={{ margin: "1rem" }}>
                            {"Επιλέξτε την Εκτροφή που σας ενδιαφέρει"}
                        </Typography>
                        <Form
                            content={ownerFormContent}
                            onSubmit={handleOwnerSubmit} />
                    </Box>
                </Box>
            )}
            {step === 2 && (
                <>
                    <AreYouSurePopup
                        width={"900px"}
                        isOpen={areYouSureIsOpen}
                        title="Αφαίρεση Ζώου"
                        titleColor="black"
                        onClose={() => setAreYouSureIsOpen(false)}
                        innerText={`Είστε σίγουροι ότι θέλετε να αφαιρέσετε το ζώο με κωδικό "${animalToUpdate?.code}";`}
                        declineText="Πίσω"
                        onDecline={() => setAreYouSureIsOpen(false)}
                        acceptText="Ναι, αφαίρεση Ζώου"
                        onAccept={() => handleDelete()}
                    />
                    <Modal open={isUpdateOpen} onClose={() => setIsUpdateOpen(false)} sx={{ display: "flex", justifyContent: "flex-end" }}>
                        <Slide direction="left" in={isUpdateOpen} mountOnEnter unmountOnExit>
                            <Box className={listClasses.modalContent}>
                                <Typography variant="h5" className={listClasses.modal_title}>
                                    Επεξεργασία Ζώου
                                </Typography>
                                <Form
                                    content={updateAnimalFormContent}
                                    onSubmit={handleAnimalUpdateSubmit}
                                />
                                {userRole === "ADMIN" ? (
                                    <Box sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}>
                                        <IconButton
                                            aria-label="delete"
                                            onClick={() => setAreYouSureIsOpen(true)}
                                        >
                                            <DeleteIcon />
                                        </IconButton >
                                    </Box>
                                ) : (
                                    <></>
                                )}
                            </Box>
                        </Slide>
                    </Modal>
                    <Grid2 container className={listClasses.container} direction="column" sx={{ marginTop: "1rem", marginLeft: "3rem" }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ width: "90%", marginBottom: "1rem" }}>
                            <Typography variant="h6" className={listClasses.form_title}>
                                {`Ζώα προς πώληση για την εκτροφή "${selectedOwner.lastName} ${selectedOwner.firstName}"`}
                            </Typography>
                        </Box>
                        <Table
                            data={fetchedAvailableAnimals}
                            noDataText={isLoading ? "Φόρτωση..." : "Δεν υπάρχουν Ζώα προς πώληση!"}
                            columns={tableColumns}
                        />
                    </Grid2>
                </>
            )}
        </>
    )
}

export default memo(EditAvailableAnimals);